// src/components/BillRouter.tsx
import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { isValidCountrySlug, isValidStateSlug } from '../utils/stateUtils';
import BillDetailPage from '../pages/BillDetailPage';
import StateBillsPage from '../pages/StateBillsPage';

/**
 * Smart router component that resolves the ambiguity for /bill/:param (1-segment)
 *
 * Disambiguation logic (per contracts/url-routing.md):
 *
 * 1. If param is a valid country slug (e.g., "united-states", "canada"):
 *    → Render StateBillsPage (shows all states for that country)
 *
 * 2. If param is a valid state slug (e.g., "texas"):
 *    → Legacy URL — redirect to /bill/united-states/:param
 *
 * 3. Otherwise:
 *    → Treat as a bill slug — render BillDetailPage (bill directory / detail lookup)
 */
const BillRouter: React.FC = () => {
  const { param } = useParams<{ param: string }>();

  if (!param) {
    return <Navigate to="/" replace />;
  }

  // Check if param is a valid country slug first
  if (isValidCountrySlug(param)) {
    return <StateBillsPage />;
  }

  // Check if param is a valid state slug → legacy URL, redirect with united-states prefix
  if (isValidStateSlug(param)) {
    return <Navigate to={`/bill/united-states/${param}`} replace />;
  }

  // Otherwise treat as a bill slug — render BillDetailPage
  return <BillDetailPage />;
};

export default BillRouter;
